import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDate(date: string | Date) {
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(date))
}

export function formatDateTime(date: string | Date) {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(date))
}

export function formatTime(date: string | Date) {
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(date))
}

export function slugify(text: string) {
  return text
    .toLowerCase()
    .replace(/[^\w ]+/g, '')
    .replace(/ +/g, '-')
}

export function capitalize(text: string) {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

export function truncate(text: string, length: number) {
  if (text.length <= length) return text
  return text.slice(0, length) + '...'
}

export function sleep(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export function getInitials(firstName: string, lastName: string) {
  return (firstName?.charAt(0) || '') + (lastName?.charAt(0) || '')
}

export function getAvatarColor(firstName: string, lastName: string) {
  const colors = [
    'bg-red-100 text-red-700',
    'bg-blue-100 text-blue-700',
    'bg-green-100 text-green-700',
    'bg-yellow-100 text-yellow-700',
    'bg-purple-100 text-purple-700',
    'bg-pink-100 text-pink-700',
    'bg-indigo-100 text-indigo-700',
    'bg-orange-100 text-orange-700',
    'bg-teal-100 text-teal-700',
    'bg-cyan-100 text-cyan-700'
  ]

  const name = `${firstName}${lastName}`.toLowerCase()
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return colors[Math.abs(hash) % colors.length]
}

function base64ToBase64url(base64: string): string {
  return base64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

const DEFAULT_CONDUCT_BASE = (() => {
  if (import.meta.env.VITE_CONDUCT_BASE_URL) {
    return import.meta.env.VITE_CONDUCT_BASE_URL.replace(/\/$/, '')
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin
  }

  return 'http://localhost:3001'
})()

export function generateConductUrl(session: any, baseUrl: string = DEFAULT_CONDUCT_BASE) {
  try {
    // Match the exact backend structure from conduct-url-encoder.ts
    const conductData = {
      sessionId: session.id,
      managerId: session.managerId,
      employeeId: session.participantId || session.employee?.id || '4', // Map participantId to employeeId for backend compatibility
      teamId: session.teamId || '1', // Use session teamId or default to '1'
      workflowType:
        session.deliveryMethod === 'email'
          ? 'email'
          : session.workflowType === 'email'
          ? 'email'
          : session.type?.toLowerCase().replace(/ /g, '') || 'physical',
      title: session.title || 'Weekly 1-on-1',
      isPublic: false,
      timestamp: Date.now(),
      ...(session.setupId && { setupId: session.setupId })
    }

    const jsonString = JSON.stringify(conductData)
    const base64 = btoa(jsonString)

    // Convert to URL-safe base64url format (as required by backend)
    const base64url = base64ToBase64url(base64)

    return `${baseUrl}/conduct/${base64url}`
  } catch (error) {
    console.error('Error generating conduct URL:', error)
    return `${baseUrl}/conduct/error`
  }
}

const ABSOLUTE_ASSET_PREFIXES = ['http://', 'https://', 'data:', 'blob:'] as const

export function getApiBaseUrl(): string {
  const rawBase = import.meta.env.VITE_API_URL || '/api'

  if (ABSOLUTE_ASSET_PREFIXES.some((prefix) => rawBase.startsWith(prefix))) {
    return rawBase.replace(/\/$/, '')
  }

  const normalizedPath = rawBase.startsWith('/') ? rawBase : `/${rawBase}`

  if (typeof window !== 'undefined' && window.location?.origin) {
    const origin = window.location.origin.replace(/\/$/, '')
    return `${origin}${normalizedPath}`.replace(/\/$/, '')
  }

  return normalizedPath.replace(/\/$/, '')
}

export function resolveAssetUrl(path: string | null | undefined): string | null {
  if (!path) {
    return null
  }

  const trimmed = path.trim()
  if (!trimmed) {
    return null
  }

  if (ABSOLUTE_ASSET_PREFIXES.some((prefix) => trimmed.startsWith(prefix))) {
    return trimmed
  }

  const base = getApiBaseUrl()
  const separator = trimmed.startsWith('/') ? '' : '/'
  return `${base}${separator}${trimmed}`
}
